// On the census page, a chart_id is used to differentiate between a real
// encounter and a placeholder encounter.  So we should rely on `checkForChart`
// to know if we should rely on the visit OR the chart for noteStatus
import React from 'react'
import { format } from 'date-fns'
import { downloadNotesPDF } from '../helper/backendHelper'
import { convertHyphenDateToSlashDate } from './sharedFunctions'
import { VISIT_STATUS } from './statusConstants'

export function getNoteStatus(visit, checkForChart = false) {
  if (visit.chart_id || (checkForChart && visit?.chart?._id)) {
    if (visit?.chart?.signed_on) {
      return 'completed'
    } else if (visit?.chart?.pending_review) {
      return 'pending_review'
    } else {
      return 'draft'
    }
  } else {
    return 'no_notes'
  }
}

export function getNoteStatuses(item, isHistory) {
  let btnStatus = 'new'
  let sentStatus = null

  if (item) {
    // if isHistory is true, then the chart is attached to the visit
    // else we need chart_id to confirm that the chart is real, and not a placeholder/SALT chart
    if (isHistory ? item?.chart_id || item?.chart?._id : item?.chart_id) {
      if (item?.chart?.signed_on) {
        // if there is no substantive OR it has been cosigned, then we are a completed note
        if (!item?.incident_to_provider_id || item?.chart?.cosigned_on) {
          btnStatus = 'complete'
          if (item?.chart?.send_status) {
            if (item?.chart.send_status === 'success') {
              sentStatus = 'success'
            } else if (
              item?.chart.send_status === 'failure' ||
              item?.chart.send_status === 'unprocessable'
            ) {
              sentStatus = 'failure'
            }
          }
        } else {
          // else there is a substantive AND it has not been cosigned - leave as draft
          btnStatus = 'edit'
        }
      } else if (item?.chart?.pending_review) {
        btnStatus = 'pending_review'
      } else {
        btnStatus = 'edit'
      }
    }
  }

  return { btnStatus, sentStatus }
}

export function getProgressNoteIcon(visit, { fontSize = '0.9em', isHistory = false, userId = '' }) {
  const { btnStatus, sentStatus } = getNoteStatuses(visit, isHistory)
  if (sentStatus) {
    if (sentStatus === 'success') {
      return (
        <i
          className="fa-solid fa-file-circle-check icons NoteSentSuccess"
          style={{ fontSize: '0.9em', marginLeft: '5px' }}
        ></i>
      )
    } else {
      return (
        <i
          className="fa-solid fa-file-circle-xmark icons NoteSentFailed"
          style={{ fontSize: '0.9em', marginLeft: '5px' }}
        ></i>
      )
    }
  } else if (btnStatus === 'complete' && visit?.chart?.cosigned_on) {
    return (
      <i
        className="far fa-files-medical"
        style={{ fontSize: fontSize, marginLeft: '5px', marginRight: '8px' }}
      ></i>
    )
  }

  // default
  const iconName =
    visit.status !== VISIT_STATUS.last && visit?.chart?.private ? 'fa-file-lock' : 'fa-file-plus'

  return (
    <i
      className={`fa-solid ${iconName} note-icon ${btnStatus}`}
      style={{
        fontSize: fontSize,
        marginLeft: '5px',
        marginRight: '8px',
        color: getProgressNoteIconColor(btnStatus),
      }}
    ></i>
  )
}

export function getProgressNoteIconColor(status) {
  if (status === 'complete') {
    return 'var(--note-completed)'
  }
  if (status === 'pending_review') {
    return 'var(--note-pending)'
  }
  if (status === 'edit') {
    return 'var(--note-draft)'
  }
  return 'var(--note-available)'
}

// if a note/chart is private, and we are not the note creator
// THEN we cannot see it (false)
export function shouldHidePrivateNote(chart, userId) {
  if (!chart || !userId) {
    return false
  }

  return Boolean(chart?.private && chart?.created_by !== userId)
}

/**
 * Determines if a provider can edit a note
 *
 * the note's visit must be a real visit, not a placeholder visit
 * the note's visit must have a rendering provider attached
 * the provider must be either the note's visit's rendering or substantive
 */
export function canProviderAccessNote(visit, provider) {
  if (!provider || !provider?._id) {
    return false
  }

  if (visit?.status === VISIT_STATUS.last || provider?.bypassSubmitValidation) {
    return true
  }

  return !(
    visit?.rendering_provider_id &&
    visit?.rendering_provider_id !== provider._id &&
    visit?.incident_to_provider_id !== provider._id
  )
}

// if SSP tries to open a note (and none exists), do NOT allow
export function canSharedServiceProviderOpenNote(visit, provider) {
  if (!provider || !provider?._id) {
    return false
  }

  if (visit.incident_to_provider_id !== provider._id) {
    // not the incident, so they can do whatever
    return true
  }

  if (
    visit?.status === VISIT_STATUS.checked_in ||
    visit?.status === VISIT_STATUS.bill_pending ||
    visit?.status === VISIT_STATUS.bill_submitted ||
    visit?.status === VISIT_STATUS.completed
  ) {
    return visit?.chart?._id
  }

  return true
}

export function doesNoteHaveAsterisks(selectedSections) {
  // search from starting point until end of active sections
  for (let i = 0; i < selectedSections.length; i++) {
    let curValue = selectedSections[i]?.values?.[0] || ''
    let nextAsteriskOccurrence = curValue.indexOf('***')
    if (nextAsteriskOccurrence > -1) {
      // we found one
      return true
    }
  }

  return false
}

export async function downloadChartPDF(visit) {
  return await downloadNotesPDF(visit._id).then((res) => {
    if (res?.status === 'success') {
      const link = document.createElement('a')
      link.href = res.data
      const fileName = `${visit?.patient_lastname}_${visit?.patient_firstname}_${format(
        new Date(convertHyphenDateToSlashDate(visit?.service_date_from)),
        'MMddyyyy',
      )}`
      link.setAttribute('download', fileName) //or any other extension
      document.body.appendChild(link)
      link.click()
    }
  })
}

export function getIsNoteRequired(practice, provider) {
  let isRequired = false
  if (practice?.note_mode || provider?.note_mode) {
    if (practice?.note_mode === 'required') {
      isRequired = provider?.note_mode !== 'optional'
    } else if (practice?.note_mode === 'optional') {
      isRequired = provider?.note_mode === 'required'
    } else {
      isRequired = provider?.note_mode === 'required'
    }
  }

  return isRequired
}

export function getChartText(chart) {
  let contents = ''
  if (chart?.data) {
    chart.data?.forEach((section) => {
      let sectionContent = ''
      if (!section?.skip) {
        section.values?.forEach((val) => {
          if (val && val.trim()) {
            contents += val + '\n'
          }
        })
      }
      if (sectionContent) {
        contents += (section?.title?.toUpperCase() || '') + '\n\n' + sectionContent + '\n\n'
      }
    })
  }

  return contents
}
